import React, { useEffect, useState, useRef, Component } from 'react';
import { LayoutDashboard, Users, FileText, ShoppingCart, MessageSquare, Sparkles, Package, Mail, Settings, Sliders, CreditCard, RefreshCw, Globe, Tag, Wrench, GitBranch, RotateCcw, Bell, Briefcase, Repeat, Activity, ChevronRight, PanelLeftClose, PanelLeft, BoxIcon, Plug, BarChart3, Award, Gift, CheckCircle2, Layers, List, ArrowRightLeft, Workflow } from 'lucide-react';
export interface SidebarProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  user?: {
    name: string;
    email: string;
    avatar?: string;
  };
  'data-id'?: string;
}
interface NavigationItem {
  name: string;
  icon: ComponentType<{
    className?: string;
  }>;
  subItems?: {
    name: string;
    icon: ComponentType<{
      className?: string;
    }>;
  }[];
}
const navigationItems: NavigationItem[] = [{
  name: 'Dashboard',
  icon: LayoutDashboard
}, {
  name: 'Cohort Retention',
  icon: Users
}, {
  name: 'Analytics',
  icon: BarChart3
}, {
  name: 'Subscriptions',
  icon: FileText
}, {
  name: 'Orders',
  icon: ShoppingCart
}, {
  name: 'AI Chat',
  icon: MessageSquare
}, {
  name: 'Club Builder',
  icon: Sparkles
}, {
  name: 'Products',
  icon: Package
}, {
  name: 'Contacts',
  icon: Mail
}, {
  name: 'Integrations',
  icon: Plug
}, {
  name: 'Settings',
  icon: Settings,
  subItems: [{
    name: 'General',
    icon: Sliders
  }, {
    name: 'Account & Billing',
    icon: CreditCard
  }, {
    name: 'Order Processing',
    icon: RefreshCw
  }, {
    name: 'Customer Portal',
    icon: Globe
  }, {
    name: 'Member Tags',
    icon: Tag
  }, {
    name: 'Automation Rules',
    icon: Workflow
  }]
}, {
  name: 'Management',
  icon: Wrench,
  subItems: [{
    name: 'Cancellation Flow',
    icon: GitBranch
  }, {
    name: 'Cancellation Tools',
    icon: BoxIcon
  }, {
    name: 'Payment Recovery',
    icon: RotateCcw
  }, {
    name: 'Notifications',
    icon: Bell
  }, {
    name: 'Migration',
    icon: ArrowRightLeft
  }]
}, {
  name: 'Loyalty',
  icon: Award,
  subItems: [{
    name: 'Loyalty Overview',
    icon: LayoutDashboard
  }, {
    name: 'Loyalty Members',
    icon: Users
  }, {
    name: 'Loyalty Rewards',
    icon: Gift
  }, {
    name: 'Rules & Tiers',
    icon: Sliders
  }]
}, {
  name: 'Allocations',
  icon: Layers,
  subItems: [{
    name: 'All Allocations',
    icon: List
  }, {
    name: 'Templates',
    icon: FileText
  }]
}, {
  name: 'Operations',
  icon: Briefcase,
  subItems: [{
    name: 'Product Replace',
    icon: Repeat
  }, {
    name: 'Activity Log',
    icon: Activity
  }, {
    name: 'Tasks',
    icon: CheckCircle2
  }, {
    name: 'Reports',
    icon: BarChart3
  }]
}];
export const Sidebar: React.FC<SidebarProps> = ({
  currentPage = 'Dashboard',
  onNavigate,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  user = {
    name: 'Sarah Chen',
    email: 'sarah@example.com'
  },
  'data-id': dataId
}) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [flyoutPosition, setFlyoutPosition] = useState<number>(0);
  const menuItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Use controlled collapsed state if provided, otherwise use internal state
  const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;
  // Auto-expand parent menu when a sub-item is active
  useEffect(() => {
    if (isCollapsed) return;
    setExpandedMenus(prev => {
      const newState = {
        ...prev
      };
      let changed = false;
      navigationItems.forEach(item => {
        if (item.subItems) {
          const hasActiveSubItem = item.subItems.some(subItem => subItem.name === currentPage);
          if (hasActiveSubItem && !newState[item.name]) {
            newState[item.name] = true;
            changed = true;
          }
        }
      });
      return changed ? newState : prev;
    });
  }, [currentPage, isCollapsed]);
  const handleNavigate = (page: string) => {
    setHoveredMenu(null); // Close flyout after navigation
    if (onNavigate) {
      onNavigate(page);
    }
  };
  const toggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    if (onCollapsedChange) {
      onCollapsedChange(newCollapsed);
    } else {
      setInternalCollapsed(newCollapsed);
    }
    // Close all expanded menus when collapsing
    if (newCollapsed) {
      setExpandedMenus({});
    }
  };
  const toggleMenu = (menuName: string) => {
    if (isCollapsed) return;
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };
  const handleParentItemClick = (item: NavigationItem) => {
    if (item.subItems && item.subItems.length > 0) {
      if (isCollapsed) {
        // In collapsed mode, clicking toggles the flyout
        setHoveredMenu(hoveredMenu === item.name ? null : item.name);
      } else {
        toggleMenu(item.name);
      }
    } else {
      handleNavigate(item.name);
    }
  };
  const handleMouseEnter = (itemName: string, hasSubItems: boolean, subItemsCount: number) => {
    if (isCollapsed && hasSubItems) {
      const ref = menuItemRefs.current[itemName];
      if (ref) {
        const rect = ref.getBoundingClientRect();
        // Calculate flyout height: header (~44px) + items (40px each) + padding (16px)
        const estimatedFlyoutHeight = 44 + subItemsCount * 40 + 16;
        // Position so bottom of flyout aligns with bottom of menu item
        const topPosition = rect.bottom - estimatedFlyoutHeight;
        setFlyoutPosition(Math.max(8, topPosition)); // Ensure at least 8px from top
      }
      setHoveredMenu(itemName);
    }
  };
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  return <aside className={`flex flex-col h-screen bg-white border-r border-ocean-gray-300 transition-all duration-300 ease-in-out ${isCollapsed ? 'w-[64px]' : 'w-[240px]'}`} style={{
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
  }} data-id="element-2678">
      {/* Logo Area */}
      <div className="flex items-center h-[56px] px-4 border-b border-ocean-gray-300" data-id="element-2679">
        {!isCollapsed && <>
            <img src="https://assets.winepik.io/winepik-logo.png" alt="Winepik Logo" className="h-14 w-auto flex-shrink-0 transition-all duration-300" data-id="element-2680" />

            <div className="flex-1" data-id="element-2681"></div>
            <button onClick={toggleCollapse} className="flex items-center justify-center w-8 h-8 rounded-md text-ocean-gray-600 hover:bg-ocean-gray-100 transition-all duration-200 focus:outline-none focus:shadow-focus flex-shrink-0" aria-label="Collapse sidebar" data-id="element-2682">
              <PanelLeftClose className="w-5 h-5" data-id="element-2683" />
            </button>
          </>}

        {isCollapsed && <div className="flex items-center justify-center w-full" data-id="element-2684">
            <button onClick={toggleCollapse} className="flex items-center justify-center w-8 h-8 rounded-md text-ocean-gray-600 hover:bg-ocean-gray-100 transition-all duration-200 focus:outline-none focus:shadow-focus" aria-label="Expand sidebar" data-id="element-2685">
              <PanelLeft className="w-5 h-5" data-id="element-2686" />
            </button>
          </div>}
      </div>

      {/* Navigation Menu */}
      <nav className={`flex-1 px-2 py-2 ${isCollapsed ? 'overflow-visible' : 'overflow-y-auto'}`} data-id="element-2687">
        {navigationItems.map(item => {
        const Icon = item.icon;
        const isActive = currentPage === item.name;
        const hasSubItems = item.subItems && item.subItems.length > 0;
        const isExpanded = expandedMenus[item.name];
        const isFlyoutOpen = isCollapsed && hoveredMenu === item.name;
        return <div key={item.name} className="relative" ref={el => {
          menuItemRefs.current[item.name] = el;
        }} onMouseEnter={() => handleMouseEnter(item.name, hasSubItems || false, item.subItems?.length || 0)} onMouseLeave={() => {
          if (isCollapsed) {
            setHoveredMenu(null);
          }
        }} data-id="element-2688">
              {/* Parent Menu Item */}
              <button onClick={() => handleParentItemClick(item)} className={`
                  relative w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'gap-[10px] px-4'} h-[40px] rounded-md
                  text-[14px] font-medium
                  transition-all duration-200 ease-in-out
                  focus:outline-none focus:shadow-focus
                  ${isActive ? 'bg-ocean-primary-light text-ocean-primary before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] before:bg-ocean-primary before:rounded-r' : 'text-ocean-gray-600 hover:bg-ocean-gray-100'}
                `} title={isCollapsed ? item.name : undefined} data-id="element-2689">
                <Icon className="w-[18px] h-[18px] flex-shrink-0" data-id="element-2690" />
                {!isCollapsed && <>
                    <span className="flex-1 truncate text-left" data-id="element-2691">
                      {item.name}
                    </span>
                    {hasSubItems && <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} data-id="element-2692" />}
                  </>}
              </button>

              {/* Flyout Menu for Collapsed State */}
              {isFlyoutOpen && hasSubItems && <div className="fixed z-[9999] min-w-[200px] bg-white border border-ocean-gray-300 rounded-lg shadow-lg py-2" style={{
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            left: '72px',
            top: `${flyoutPosition}px`
          }} onMouseEnter={() => setHoveredMenu(item.name)} onMouseLeave={() => setHoveredMenu(null)} data-id="element-2693">
                  {/* Invisible bridge to connect sidebar item to flyout */}
                  <div className="absolute top-0 bottom-0 right-full w-[16px]" style={{
              background: 'transparent'
            }} data-id="element-2694" />

                  {/* Flyout Header */}
                  <div className="px-4 py-2 border-b border-ocean-gray-200 mb-1" data-id="element-2695">
                    <span className="text-[13px] font-semibold text-ocean-gray-700" data-id="element-2696">
                      {item.name}
                    </span>
                  </div>
                  {/* Flyout Items */}
                  {item.subItems!.map(subItem => {
              const SubIcon = subItem.icon;
              const isSubActive = currentPage === subItem.name;
              return <button key={subItem.name} onClick={e => {
                e.stopPropagation();
                handleNavigate(subItem.name);
              }} className={`
                          w-full flex items-center gap-3 px-4 py-2 text-[14px] font-medium
                          transition-all duration-150 ease-in-out
                          ${isSubActive ? 'bg-ocean-primary-light text-ocean-primary' : 'text-ocean-gray-600 hover:bg-ocean-gray-100'}
                        `} data-id="element-2697">
                        <SubIcon className="w-[16px] h-[16px] flex-shrink-0" data-id="element-2698" />
                        <span className="truncate" data-id="element-2699">{subItem.name}</span>
                      </button>;
            })}
                </div>}

              {/* Sub-menu Items for Expanded State */}
              {!isCollapsed && hasSubItems && isExpanded && <div className="mt-1 mb-1" data-id="element-2700">
                  {item.subItems!.map(subItem => {
              const SubIcon = subItem.icon;
              const isSubActive = currentPage === subItem.name;
              return <button key={subItem.name} onClick={() => handleNavigate(subItem.name)} className={`
                          relative w-full flex items-center gap-[10px] pl-[32px] pr-4 h-[40px] rounded-md
                          text-[14px] font-medium
                          transition-all duration-200 ease-in-out
                          focus:outline-none focus:shadow-focus
                          ${isSubActive ? 'bg-ocean-primary-light text-ocean-primary before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] before:bg-ocean-primary before:rounded-r' : 'text-ocean-gray-500 hover:bg-ocean-gray-100 hover:text-ocean-gray-600'}
                        `} data-id="element-2701">
                        <SubIcon className="w-[16px] h-[16px] flex-shrink-0" data-id="element-2702" />
                        <span className="truncate" data-id="element-2703">{subItem.name}</span>
                      </button>;
            })}
                </div>}
            </div>;
      })}
      </nav>

      {/* User Profile Section - Static Display */}
      <div className="p-4 border-t border-ocean-gray-300" data-id="element-2704">
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} p-3`} data-id="element-2705">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{
          background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
          color: '#FFFFFF'
        }} data-id="element-2706">
            <span className="text-[14px] font-semibold" data-id="element-2707">
              {getInitials(user.name)}
            </span>
          </div>

          {/* Name & Email - Only show when not collapsed */}
          {!isCollapsed && <div className="flex-1 min-w-0 text-left" data-id="element-2708">
              <div className="text-[14px] font-medium text-ocean-gray-900 truncate" data-id="element-2709">
                {user.name}
              </div>
              <div className="text-[13px] text-ocean-gray-400 truncate" data-id="element-2710">
                {user.email}
              </div>
            </div>}
        </div>
      </div>
    </aside>;
};